import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson, notify } from "../../../utils/http";

export default function EditarProductoContrato({ initial, onClose, onSaved }) {
  // Estados editables
  const [qnProducto, setQnProducto] = useState("");
  const [valorMontoDescuento, setValorMontoDescuento] = useState("");
  const [valorMonto, setValorMonto] = useState("");

  // Estados solo lectura
  const [contratoCodigo, setContratoCodigo] = useState("");
  const [productoNombre, setProductoNombre] = useState("");
  const [qnUtilizado, setQnUtilizado] = useState("");
  const [valorMontoUtilizado, setValorMontoUtilizado] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Funciones auxiliares
  const formatNumber = (value) => {
    if (!value) return "";
    const digits = value.toString().replace(/\D/g, "");
    if (!digits) return "";
    return Number(digits).toLocaleString("es-PY");
  };

  const cleanNumber = (val) => {
    if (!val) return null;
    const digits = val.toString().replace(/\D/g, "");
    return digits ? Number(digits) : null;
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (initial) {
      // Campos editables: convertir valores numéricos a string con formato
      setQnProducto(formatNumber(initial.qnProducto));
      setValorMontoDescuento(formatNumber(initial.valorMontoDescuento));
      setValorMonto(formatNumber(initial.valorMonto));

      // Campos solo lectura
      setContratoCodigo(initial.contratoCodigo || "");
      setProductoNombre(initial.productoNombre || "");
      setQnUtilizado(formatNumber(initial.qnUtilizado));
      setValorMontoUtilizado(formatNumber(initial.valorMontoUtilizado));
    }
  }, [initial]);

  // Validación de negocio: solo (cantidad+descuento) o (monto)
  const tieneProducto = qnProducto && valorMontoDescuento;
  const tieneMonto = valorMonto && !qnProducto && !valorMontoDescuento;
  const esValido = (tieneProducto || tieneMonto) && !(tieneProducto && valorMonto);

  // Validación general (que haya al menos algún campo)
  const isValid = (qnProducto || valorMontoDescuento || valorMonto) && esValido;

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!isValid || saving) return;

    setSaving(true);
    setError("");

    // Construir payload con valores limpios
    const payload = {
      qnProducto: cleanNumber(qnProducto),
      valorMontoDescuento: cleanNumber(valorMontoDescuento),
      valorMonto: cleanNumber(valorMonto),
      // Los utilizados no se envían (se mantienen en BD)
    };

    try {
      const url = apiRoutes.contratoDetalle.editarUrl(initial.id);
      const updated = await appFetchJson(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      notify({ type: "success", message: "Producto actualizado correctamente" });
      onSaved(updated);
      onClose();
    } catch (e) {
      console.error("Error editando producto en contrato:", e);
      setError(e?.message || "Error al actualizar el producto.");
      notify({ type: "error", message: e?.message || "Error al actualizar el producto." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Editar Producto del Contrato</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          <p className="text-gray-600 mb-4 italic">
            Modifique los valores del producto asociado al contrato.
          </p>

          {error && <div className="text-red-600 mb-3 font-medium">{error}</div>}

          <div className="form-grid">
            {/* Contrato (solo lectura) */}
            <div className="form-row col-span-2">
              <label className="form-label">Contrato</label>
              <input
                type="text"
                className="input w-full bg-gray-100 cursor-not-allowed"
                value={contratoCodigo}
                disabled
                readOnly
              />
            </div>

            {/* Producto (solo lectura) */}
            <div className="form-row col-span-2">
              <label className="form-label">Producto</label>
              <input
                type="text"
                className="input w-full bg-gray-100 cursor-not-allowed"
                value={productoNombre}
                disabled
                readOnly
              />
            </div>

            <div className="col-span-2 border-t border-gray-200 my-2"></div>

            {/* Campos editables mutuamente excluyentes */}
            <div className="form-row col-span-2">
              <label className="form-label">Cantidad de Producto</label>
              <input
                type="text"
                className="input w-full"
                value={qnProducto}
                onChange={(e) => setQnProducto(formatNumber(e.target.value))}
                placeholder="Ej: 100"
              />
            </div>

            <div className="form-row col-span-2">
              <label className="form-label">Valor de Descuento Producto</label>
              <input
                type="text"
                className="input w-full"
                value={valorMontoDescuento}
                onChange={(e) => setValorMontoDescuento(formatNumber(e.target.value))}
                placeholder="Ej: 5.000"
              />
            </div>

            <div className="form-row col-span-2">
              <label className="form-label">Valor de Monto PYG</label>
              <input
                type="text"
                className="input w-full"
                value={valorMonto}
                onChange={(e) => setValorMonto(formatNumber(e.target.value))}
                placeholder="Ej: 1.500.000"
              />
            </div>

            <div className="col-span-2 border-t border-gray-200 my-2"></div>

            {/* Campos solo lectura (utilizados) */}
            <div className="form-row col-span-2">
              <label className="form-label">Cantidad Producto Utilizado</label>
              <input
                type="text"
                className="input w-full bg-gray-100 cursor-not-allowed"
                value={qnUtilizado || "0"}
                disabled
                readOnly
              />
            </div>

            <div className="form-row col-span-2">
              <label className="form-label">Valor de Monto PYG Utilizado</label>
              <input
                type="text"
                className="input w-full bg-gray-100 cursor-not-allowed"
                value={valorMontoUtilizado || "0"}
                disabled
                readOnly
              />
            </div>

            {!esValido && (qnProducto || valorMontoDescuento || valorMonto) && (
              <div className="col-span-2 text-red-500 text-sm mt-1">
                Solo puede cargar (Cantidad + Descuento) o (Valor Monto), no ambos.
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              label={saving ? "Guardando..." : "Guardar Cambios"}
              isValid={isValid}
              saving={saving}
              onClick={handleSave}
            />
            <BtnCancelar onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}